/**
 * Shared primitives for the client-specific optimizer breakdown views.
 *
 * These started inside CrimtanOptimizationBreakdown.js and moved here when MMGY got a
 * breakdown of its own. Nothing in this file is client-specific: it is the render-safety
 * layer and the visual vocabulary that any client's breakdown needs, and a second client
 * importing it from a file named after the first is how the shared parts stop being
 * recognised as shared.
 *
 * The render-safety part is the reason this is not just styling. Every value in an
 * optimizer payload comes from a model answering a prompt, with no schema at ANY layer —
 * not in campaign-services, not in app-backend, not here. These helpers are what stand
 * between that and the activity feed.
 */

const tone = (level) => {
  const l = String(level || '').toLowerCase();
  if (l === 'high' || l === 'overpacing' || l === 'blocked') return { bg: '#fee', color: '#c00' };
  if (l === 'medium' || l === 'underpacing') return { bg: '#fef3c7', color: '#b45309' };
  if (l === 'low' || l === 'on_pace' || l === 'on pace' || l === 'ok') return { bg: '#E8FAF2', color: '#0F7B5F' };
  return { bg: '#eef', color: '#3451b2' };
};

export const Badge = ({ level, children }) => {
  const t = tone(level);
  return (
    <span style={{
      background: t.bg, color: t.color, padding: '2px 8px', borderRadius: '10px',
      fontSize: '0.72em', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.4px',
      whiteSpace: 'nowrap',
    }}>{children}</span>
  );
};

export const cardStyle = {
  border: '1px solid #e5e7eb', borderRadius: '6px', padding: '12px',
  marginTop: '8px', background: '#fff',
};

export const SEVERITY_RANK = { HIGH: 0, MEDIUM: 1, LOW: 2 };

/** Values that mean "nothing here" when a model fills a slot it had no answer for. */
const EMPTY_TOKENS = ['none', 'n/a', 'na', 'null'];

/**
 * Render-safe text for an LLM-supplied field.
 *
 * When a field that is meant to be a string arrives as an object (e.g.
 * `evidence: {reason: "..."}`), React throws "Objects are not valid as a React child", and
 * because these sections render inside the shared activity feed that throw unmounts the
 * WHOLE feed, not just the card. So every model-supplied value is funnelled through here
 * before it reaches JSX.
 */
export function text(value) {
  if (value == null) return null;
  if (typeof value === 'string') return value.trim() || null;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
}

/** Blocker list, normalised: tolerates a bare string and drops the "NONE" placeholder. */
export function realBlockers(value) {
  const list = Array.isArray(value) ? value : (value == null ? [] : [value]);
  return list
    .map(text)
    .filter((b) => b && !EMPTY_TOKENS.includes(b.toLowerCase()));
}

/**
 * Whether a diagnosis entry can serve.
 *
 * Reads BOTH signals rather than trusting `can_deliver` alone. The model sets the flag and
 * the blockers list independently, so they can disagree — and the expensive direction of
 * that disagreement is one-sided: filing a genuinely blocked line item under "can deliver"
 * green-badges it AND hides it behind the collapsed disclosure, which is exactly the thing
 * these sections exist to surface. An entry with real blockers is treated as blocked
 * whatever the flag says.
 */
export function isBlocked(entry) {
  if (entry?.can_deliver === false) return true;
  if (String(entry?.can_deliver).toLowerCase() === 'false') return true;
  return realBlockers(entry?.blockers).length > 0;
}

/** Section shell so every part of a breakdown is visually one system. */
export const Section = ({ step, title, subtitle, children }) => (
  <div style={{ marginBottom: '22px' }}>
    <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px', flexWrap: 'wrap', marginBottom: '2px' }}>
      <span style={{
        background: '#111827', color: '#fff', width: '20px', height: '20px', borderRadius: '50%',
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        fontSize: '0.7em', fontWeight: 700, flexShrink: 0,
      }}>{step}</span>
      <h5 style={{ margin: 0, color: '#111827' }}>{title}</h5>
    </div>
    {subtitle && (
      <p style={{ margin: '0 0 8px 28px', fontSize: '0.8em', color: '#6b7280' }}>{subtitle}</p>
    )}
    {children}
  </div>
);

/**
 * "id | name" is how the optimizer is told to quote catalog entries (an audience id or an
 * inventorySourceId followed by its display name), because an id with no name is unreadable
 * and a name with no id is not assignable. Split it so the id can be shown as the copyable
 * thing it is; fall back to rendering the value verbatim when it carries no separator.
 */
export function splitCatalogEntry(value) {
  // Coerced, not type-checked: a bare numeric id is a usable audience reference and must not
  // be dropped just because the model emitted it unquoted.
  const raw = text(value);
  if (!raw) return null;
  const idx = raw.indexOf('|');
  const entry = idx === -1
    ? { id: null, name: raw }
    : { id: raw.slice(0, idx).trim() || null, name: raw.slice(idx + 1).trim() || null };
  // "999 |" leaves no name and "| x" leaves no id; only a value with neither is unrenderable.
  if (!entry.id && !entry.name) return null;
  if (entry.name && EMPTY_TOKENS.includes(entry.name.toLowerCase()) && !entry.id) {
    return null;
  }
  return entry;
}

export const IdChip = ({ children }) => (
  <code style={{
    background: '#f3f4f6', color: '#374151', padding: '1px 6px', borderRadius: '3px',
    fontSize: '0.85em', fontFamily: 'monospace',
  }}>{children}</code>
);

/**
 * Key issues ordered by severity, HIGH first.
 *
 * Entries without a recognised severity keep their original relative order and sink below
 * the ranked ones, so an unlabelled issue is never promoted above a HIGH one.
 */
export function rankIssuesBySeverity(keyIssues) {
  return (Array.isArray(keyIssues) ? keyIssues : [])
    .map((issue, i) => [issue, i])
    .sort((a, b) => {
      const ra = SEVERITY_RANK[String(a[0]?.severity ?? '').toUpperCase()];
      const rb = SEVERITY_RANK[String(b[0]?.severity ?? '').toUpperCase()];
      if (ra == null && rb == null) return a[1] - b[1];
      if (ra == null) return 1;
      if (rb == null) return -1;
      return ra - rb || a[1] - b[1];
    })
    .map(([issue]) => issue);
}
